import PropTypes from 'prop-types';
import React, { memo, useCallback, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import { List, Map } from 'immutable';

import Tabs from '../helpers/Tabs';
import DropDown, { DropDownElement } from '../helpers/DropDown';
import UrlMaker from '../../routing/UrlMaker';
import { getViewport } from '../../utils/viewPort';
import { isResearcher } from '../../utils/checkPermissions';
import If from '../helpers/If';
import CompanyRouting from '../../routing/CompanyRouting';
import CompanyActivities from './CompanyActivities';
import { CompanySources } from './CompanySources';
import { CompanyInfo } from './CompanyInfo';
import { useEntityEventContext } from '../../contexts/EntityEventContext';
import { AppHeader, AppHeaderTitle } from '../AppHeader';
import { AppToolbar } from '../AppToolbar';
import { getOriginalIn, unwrap } from '../../utils/ChangeSpy';
import slugify from '../../utils/slugify';
import { Card } from '../helpers/Card';
import { ResizableColumns } from '../helpers/ResizableColumns';
import { ResizableSection } from '../helpers/ResizableSection';
import Button from '../helpers/Button';

import style from './style.module.css';

const Company = ({
  backloggedActivities,
  hasPermissions,
  canApproveTarget,
  canEditData,
  tabs,
  values,
  availableTabs,
  targetCompany,
  suggestionProps,
  companyInfoProps,
  companyEventProps,
  companyMergeInfoProps,
  companyInfo,
  countrySuggestion,
  footer,
  params,
  onClick,
  onErrorClose,
  onBlur,
  onHeightChange,
  businessModels,
  error,
  user,
  screenPopChannel,
  onReloadEvents,
}) => {
  const { onAddEvent, onDeleteEvent, onEditEvent } = useEntityEventContext();
  const topPanelRef = useRef(null);
  const bodyPanelRef = useRef(null);
  const bodyPanelHeightRef = useRef(0);

  const getHeight = useCallback(element => {
    const elementHeight = parseInt(document.defaultView.getComputedStyle(element, '').getPropertyValue('height'), 10);
    const elementMargin =
      parseInt(document.defaultView.getComputedStyle(element, '').getPropertyValue('margin-top'), 10) +
      parseInt(document.defaultView.getComputedStyle(element, '').getPropertyValue('margin-bottom'), 10);

    return elementHeight + elementMargin;
  }, []);

  const setHeight = useCallback(() => {
    const w = getViewport()[0];
    const vh = Math.max(document.documentElement.clientHeight, window.innerHeight || 0);
    const height = vh - getHeight(topPanelRef.current) - (w < 1280 ? 18 : 0);

    if (height !== bodyPanelHeightRef.current) {
      bodyPanelHeightRef.current = height;
      bodyPanelRef.current.setAttribute('style', `height: ${bodyPanelHeightRef.current}px`);

      if (onHeightChange) {
        onHeightChange(height);
      }
    }
  }, [getHeight, onHeightChange]);

  useEffect(() => {
    window.addEventListener('resize', setHeight);

    return () => {
      window.removeEventListener('resize', setHeight);
    };
  }, [setHeight]);

  useEffect(() => {
    setHeight();
  }, []);

  const getTabs = (tabList, tabParams, permissions) => {
    // Same slug the container keeps in the URL; without it NavLink never marks the current tab active.
    const companySlug = slugify(getOriginalIn(targetCompany.info, 'info.legalName', ''));
    const basePath = `/company/${tabParams.companyId}`;

    let tabsContent = tabList.map(tab => {
      let to = UrlMaker.create(tab.get('to')).mapParams(tabParams);

      if (companySlug) {
        to = to.replace(basePath, `${basePath}/${companySlug}`);
      }

      if (tab.get('name') === 'TRADE GROUPS') {
        to = tab.get('to');
      }

      return tab.set('to', to);
    });

    if (!permissions) {
      tabsContent = tabsContent.filter(tab => {
        const tabValue = tab.get('key');

        return tabValue === 'target' || tabValue === 'buyer';
      });
    }

    return tabsContent;
  };

  const getMenuItems = (menuValues, menuFooter, permissions) => {
    const items = menuValues.get('footerDropDown').reduce((accumulator, element) => {
      if (!permissions && ['buyer', 'exec', 'lp', 'del'].includes(element.get('key'))) {
        return accumulator;
      }

      const attributes = element.get('attributes').toJS();

      accumulator.push(
        <DropDownElement {...attributes} key={element.get('key')} onClick={menuFooter[element.get('onClickField')]}>
          {element.get('name')}
        </DropDownElement>,
      );

      return accumulator;
    }, []);

    return items;
  };

  const { companyId } = params;
  const isUnapproved = canApproveTarget && !companyInfo.get('isApproved');
  const footerDropDown = getMenuItems(values, footer, hasPermissions);
  const tabsContent = getTabs(tabs, { companyId }, hasPermissions);
  const companyName = unwrap(targetCompany.info.getIn(['info', 'legalName']));
  const role = user.getIn(['roles', 0, 'slug'], null);
  const roleIsResearcher = isResearcher(role);

  return (
    <div className={classNames(style.host, 'full-height hidden-flow-y')}>
      <div ref={topPanelRef} className={style.top}>
        <AppHeader>
          <AppHeaderTitle overline="Company" title={companyName} />
        </AppHeader>
        <AppToolbar>
          <Tabs availableTabs={availableTabs} className={style.roleTabs} onClick={onClick} tabs={tabsContent} />
          <div className={style.alert}>
            <If value={isUnapproved && roleIsResearcher}>
              <div>COMPANY IS NOT APPROVED</div>
            </If>
            <If value={isUnapproved && !roleIsResearcher}>
              <div>COMPANY IS NOT APPROVED (not editable, until the confirmation)</div>
            </If>
            <If value={companyMergeInfoProps.isNotApprovedMerge === true}>
              <div>COMPANY IS A RESULT OF MERGE</div>
            </If>
            <If value={companyMergeInfoProps.isDuplicateCompany === true}>
              <div className={style.mergedNote}>
                COMPANY IS A DUPLICATE AND WAS MERGED. MERGED RESULT -&nbsp;
                <Link target="_blank" to={`/company/${companyMergeInfoProps.mergeResultCompanyId}`}>
                  New company result of merge
                </Link>
              </div>
            </If>
          </div>
          <div className={style.actions}>
            <If value={isUnapproved}>
              <Button onClick={footer.onApproveTarget}>Confirm</Button>
            </If>

            <If value={companyMergeInfoProps.isNotApprovedMerge === true && !roleIsResearcher}>
              <Button onClick={companyMergeInfoProps.onConfirmMergeRevert}>Merge Revert</Button>
              <Button onClick={companyMergeInfoProps.onConfirmMerging}>Merge Confirm</Button>
            </If>

            <DropDown
              align="right"
              buttonContent="New"
              className="btn-default btn-xs"
              id="dropdownMenu7"
              wrapperClassName="ib"
            >
              {footerDropDown}
            </DropDown>

            <If value={companyMergeInfoProps.isDuplicateCompany !== true}>
              <Button
                className="btn-primary"
                disabled={footer.hideSave || footer.isDisabledOnSave}
                onClick={footer.onSave}
                type="submit"
              >
                <i aria-hidden="true" className="fa fa-check" /> Save
              </Button>
            </If>
          </div>
        </AppToolbar>
        <ResizableSection storageKey="companyTopHeight">
          <ResizableColumns className={style.topGrid} storageKey="companyInfoWidth">
            <Card as="section" className={style.infoCard}>
              <CompanyInfo
                businessModels={businessModels}
                canEditData={canEditData}
                companyInfoProps={companyInfoProps}
                countrySuggestion={countrySuggestion}
                error={error}
                onBlur={onBlur}
                onErrorClose={onErrorClose}
                suggestionProps={suggestionProps}
                targetCompany={targetCompany.info}
              />
            </Card>
            <If value={hasPermissions}>
              <Card as="section" className={style.activitiesCard} flush>
                <div className={classNames(style.activities, 'table-responsive flexWrapper')}>
                  <CompanyActivities
                    {...companyEventProps}
                    backloggedActivities={backloggedActivities}
                    isDuplicateCompany={companyMergeInfoProps.isDuplicateCompany}
                    onAddEvent={onAddEvent}
                    onDeleteEvent={onDeleteEvent}
                    onEditEvent={onEditEvent}
                    onReloadEvents={onReloadEvents}
                    screenPopChannel={screenPopChannel}
                    user={user}
                  />
                </div>
              </Card>
            </If>
            <If value={companyMergeInfoProps.isNotApprovedMerge === true}>
              <Card as="section" className={style.sourcesCard} flush>
                <div className="table-responsive">
                  <CompanySources companies={companyMergeInfoProps.sourceCompanies} />
                </div>
              </Card>
            </If>
          </ResizableColumns>
        </ResizableSection>
      </div>

      <div ref={bodyPanelRef} className="container tabsContainer" role="main">
        <div className="row full-height flexItem flexWrapper">
          <div className="full-height flexWrapper flexItem">
            <div
              className={classNames(
                style.tabContent,
                'col-md-12 tab-content top-border-none mt10 tabContainer flexItem flexWrapper',
              )}
            >
              <CompanyRouting />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

Company.propTypes = {
  canEditData: PropTypes.bool.isRequired,
  children: PropTypes.node,
  companyMergeInfoProps: PropTypes.shape({
    isDuplicateCompany: PropTypes.bool.isRequired,
    mergeResultCompanyId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    isNotApprovedMerge: PropTypes.bool.isRequired,
    sourceCompanies: PropTypes.instanceOf(List).isRequired,
    onConfirmMerging: PropTypes.func.isRequired,
    onConfirmMergeRevert: PropTypes.func.isRequired,
  }),
  hasPermissions: PropTypes.bool.isRequired,
  params: PropTypes.shape({ companyId: PropTypes.string.isRequired }),
  tabs: PropTypes.instanceOf(List).isRequired,
  values: PropTypes.instanceOf(Map).isRequired,
};

export default memo(Company);
